// src/pages/Automations/components/AutomationTrigger.jsx
// Individual trigger card with toggle and edit button (Light theme)
// Updated: All automations now send at 1 PM Eastern (no per-agent send hours)

import React from 'react';
import { Mail, MessageSquare, Edit2, Clock, AlertCircle } from 'lucide-react';

export function AutomationTrigger({
  trigger,
  enabled,
  onToggle,
  onEdit,
  template,
  categoryColor,
  isPaused = false  // Global/trigger-level pause indicator
}) {
  return (
    <div className="bg-gray-50 rounded-xl p-4 border border-gray-200 hover:border-gray-300 transition-all">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        {/* Left Side - Info */}
        <div className="flex-1 min-w-0">
          {/* Trigger Name */}
          <h4 className="font-semibold text-gray-800 text-base">{trigger.name}</h4>

          {/* Explanation */}
          <p className="text-sm text-gray-500 mt-1">{trigger.explanation}</p>

          {/* Channel Badges */}
          <div className="flex items-center gap-2 mt-3">
            {trigger.channels.includes('sms') && (
              <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-blue-100 text-blue-700 text-xs font-medium rounded-full">
                <MessageSquare className="w-3.5 h-3.5" />
                SMS
              </span>
            )}
            {trigger.channels.includes('email') && (
              <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-purple-100 text-purple-700 text-xs font-medium rounded-full">
                <Mail className="w-3.5 h-3.5" />
                Email
              </span>
            )}
            {trigger.channels.includes('alert') && (
              <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-amber-100 text-amber-700 text-xs font-medium rounded-full">
                <AlertCircle className="w-3.5 h-3.5" />
                Alert/Task
              </span>
            )}
          </div>
        </div>

        {/* Right Side - Actions */}
        <div className="flex items-center gap-3">
          {/* Pause Indicator (if paused globally or at trigger level) */}
          {isPaused && (
            <div className="flex items-center gap-1.5 px-2 py-1.5 bg-red-50 border border-red-200 rounded-lg">
              <AlertCircle className="w-4 h-4 text-red-500" />
              <span className="text-xs font-medium text-red-600">Paused</span>
            </div>
          )}

          {/* Send Time (Fixed - 1 PM Eastern) */}
          <div className="flex items-center gap-1.5 px-2 py-1.5 bg-white border border-gray-200 rounded-lg">
            <Clock className="w-4 h-4 text-gray-500" />
            <span className="text-sm text-gray-700 font-medium">1 PM ET</span>
          </div>

          {/* Edit Button */}
          <button
            onClick={onEdit}
            className="flex items-center gap-2 px-3 py-2 bg-white border border-gray-300 hover:bg-gray-50 text-gray-700 text-sm font-medium rounded-lg transition-colors"
          >
            <Edit2 className="w-4 h-4" />
            <span className="hidden sm:inline">Edit</span>
          </button>

          {/* Toggle Switch */}
          <button
            onClick={() => onToggle(!enabled)}
            disabled={isPaused}
            className={`relative w-14 h-7 rounded-full transition-colors ${
              isPaused
                ? 'bg-gray-200 cursor-not-allowed'
                : enabled
                ? 'bg-green-500'
                : 'bg-gray-300'
            }`}
            role="switch"
            aria-checked={enabled}
            title={isPaused ? 'This automation is paused by administrator' : ''}
          >
            <span
              className={`absolute top-1 w-5 h-5 bg-white rounded-full shadow-md transition-transform ${
                enabled && !isPaused ? 'left-8' : 'left-1'
              }`}
            />
            <span className="sr-only">{enabled ? 'Enabled' : 'Disabled'}</span>
          </button>
        </div>
      </div>
    </div>
  );
}

export default AutomationTrigger;
